// components/StatePicker.tsx
//
// Searchable state/province dropdown, backed by the offline
// `country-state-city` dataset (no network call, works for every country).
// Replaces free-text state/province inputs so tax-region matching
// (computeTaxBreakdown's sellerRegion/buyerRegion comparison) never
// breaks on typos or casing again — the value stored is always the
// dataset's canonical state name.

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { State } from 'country-state-city';

interface StatePickerProps {
  countryCode: string;      // ISO 3166-1 alpha-2, e.g. "IN", "US"
  value: string;            // currently selected state name
  onChange: (stateName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  allowFlipUpward?: boolean; // opt-in: flips the menu above the field when there's
                              // no room below (e.g. inside a scrollable modal).
                              // Defaults to false so every existing usage keeps
                              // its old always-downward behavior unchanged.
}

export default function StatePicker({ countryCode, value, onChange, placeholder, disabled, allowFlipUpward = false }: StatePickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const states = useMemo(() => {
    if (!countryCode) return [];
    return State.getStatesOfCountry(countryCode.toUpperCase()) || [];
  }, [countryCode]);

  // No states in the dataset for this country (e.g. some smaller
  // countries/territories) — fall back to a plain text input so the
  // person can still type something, rather than showing a dead-end
  // empty dropdown.
  const hasStates = states.length > 0;

  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (ref.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Position for the portaled menu — since it renders in document.body
  // instead of inline here, we compute fixed screen coordinates from this
  // wrapper's bounding box, and keep them updated while open (e.g. if the
  // modal underneath is scrolled).
  const MENU_MAX_HEIGHT = 280;
  const [coords, setCoords] = useState<{ top: number; left: number; width: number; openUpward: boolean } | null>(null);
  useEffect(() => {
    if (!open || !ref.current) return;
    const updateCoords = () => {
      const rect = ref.current!.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Flip upward when there isn't enough room below but there IS
      // enough (or more) room above — same behavior as a native <select>.
      // Only enabled where the caller opts in via allowFlipUpward.
      const openUpward = allowFlipUpward && spaceBelow < MENU_MAX_HEIGHT && spaceAbove > spaceBelow;
      const top = openUpward
        ? Math.max(8, rect.top - MENU_MAX_HEIGHT - 4)
        : rect.bottom + 4;
      // Clamp left so a wide menu on a narrow field never runs off-screen.
      const width = Math.max(rect.width, 220);
      const left = Math.min(rect.left, window.innerWidth - width - 8);
      setCoords({ top, left: Math.max(8, left), width, openUpward });
    };
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [open]);

  const filtered = search
    ? states.filter(s => s.name.toLowerCase().includes(search.toLowerCase()))
    : states;

  if (!hasStates) {
    return (
      <input
        type="text"
        value={value}
        disabled={disabled}
        placeholder={placeholder || 'State / Province'}
        onChange={e => onChange(e.target.value)}
        style={{ padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 12, outline: 'none', minWidth: 200, boxSizing: 'border-box' }}
      />
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', minWidth: 200 }}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', textAlign: 'left', padding: '6px 10px', border: '1px solid #e2e8f0',
          borderRadius: 8, fontSize: 12, background: disabled ? '#f8fafc' : '#fff', cursor: disabled ? 'not-allowed' : 'pointer',
          color: value ? '#0f172a' : '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6,
          boxSizing: 'border-box',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || placeholder || 'Select State / Province'}
        </span>
        <span style={{ color: '#94a3b8', flexShrink: 0 }}>▾</span>
      </button>
      {open && coords && createPortal(
        <div ref={menuRef} style={{
          position: 'fixed', top: coords.top, left: coords.left, zIndex: 9999,
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 12px 32px rgba(0,0,0,0.15)', width: coords.width,
          maxHeight: Math.min(MENU_MAX_HEIGHT, window.innerHeight - 16),
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          <input
            autoFocus
            type="text"
            placeholder="Search state..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ padding: '8px 10px', border: 'none', borderBottom: '1px solid #e2e8f0', fontSize: 12, outline: 'none', boxSizing: 'border-box' }}
          />
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '10px 12px', fontSize: 12, color: '#94a3b8' }}>No matches</div>
            )}
            {filtered.map(s => (
              <button
                key={s.isoCode}
                type="button"
                onClick={() => { onChange(s.name); setOpen(false); setSearch(''); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px',
                  background: s.name === value ? '#eff6ff' : 'transparent', border: 'none', cursor: 'pointer',
                  fontSize: 12, color: s.name === value ? '#2563eb' : '#374151', fontWeight: s.name === value ? 700 : 400,
                }}
                onMouseEnter={e => (e.currentTarget.style.background = s.name === value ? '#eff6ff' : '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = s.name === value ? '#eff6ff' : 'transparent')}
              >
                {s.name}
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}